import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationPickerModal from '../../components/LocationPickerModal';
import { useLocation2 } from '../../contexts/LocationContext';
import { useCustomer } from '../../contexts/CustomerContext';
import api from '../../lib/api';
import './la-cafe.css';

const LOGO_SRC = '/assets/images/la-cafe-logo.png';

const HomeLanding = () => {
  const navigate = useNavigate();
  const { locations, selectedCafeLocation, setSelectedLocation, setSelectedCafeLocation } = useLocation2();
  const { customer } = useCustomer();
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pendingOrderRedirect, setPendingOrderRedirect] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [orderingOpen, setOrderingOpen] = useState(true);

  // Determine which location's site status to use (selected, or first available)
  const statusLocationId = selectedCafeLocation?.id || locations?.[0]?.id;

  useEffect(() => {
    if (!statusLocationId) return;
    let cancelled = false;
    api.getSiteStatus(statusLocationId)
      .then((s) => { if (!cancelled) setOrderingOpen(s?.is_open !== false); })
      .catch(() => { if (!cancelled) setOrderingOpen(true); });
    return () => { cancelled = true; };
  }, [statusLocationId]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    // Reveal-on-scroll
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let io;
    if (!reduce) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.12 });
      document.querySelectorAll('.la-reveal').forEach((el) => io.observe(el));
    }
    // Remove the lightning strike overlay once the intro has finished
    const timer = setTimeout(() => {
      const s = document.getElementById('la-strike');
      const f = document.getElementById('la-flash');
      if (s) s.remove();
      if (f) f.remove();
    }, 3200);
    return () => {
      if (io) io.disconnect();
      clearTimeout(timer);
    };
  }, []);

  const handleViewMenu = (e) => {
    if (e) e.preventDefault();
    if (!selectedCafeLocation) {
      setShowLocationPicker(true);
    } else {
      navigate('/menu-catalog');
    }
  };

  const handleOrderOnline = (e) => {
    if (e) e.preventDefault();
    if (!customer) {
      navigate('/customer-auth');
      return;
    }
    if (!selectedCafeLocation) {
      setPendingOrderRedirect(true);
      setShowLocationPicker(true);
    } else {
      navigate('/menu-catalog');
    }
  };

  const handleLocationSelect = (loc) => {
    setSelectedLocation(loc);
    setSelectedCafeLocation(loc);
    setShowLocationPicker(false);
    setPendingOrderRedirect(false);
    navigate('/menu-catalog');
  };

  return (
    <div className="la-root" data-testid="la-cafe-landing">
      <div id="la-flash" aria-hidden="true"></div>
      <div id="la-strike" aria-hidden="true">
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
          <defs>
            <filter id="bglow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="7" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          <g filter="url(#bglow)">
            <path className="bolt glow" pathLength="100" d="M 1520 40 L 1130 215 L 1225 255 L 855 425 L 952 468 L 545 645 L 635 685 L 110 920" />
            <path className="bolt glow branch" pathLength="100" d="M 1225 255 L 1308 408 L 1252 418 L 1318 560" />
            <path className="bolt glow branch" pathLength="100" d="M 952 468 L 872 628 L 928 638 L 838 800" />
          </g>
          <path className="bolt core" pathLength="100" d="M 1520 40 L 1130 215 L 1225 255 L 855 425 L 952 468 L 545 645 L 635 685 L 110 920" />
          <path className="bolt core branch" pathLength="100" d="M 1225 255 L 1308 408 L 1252 418 L 1318 560" />
          <path className="bolt core branch" pathLength="100" d="M 952 468 L 872 628 L 928 638 L 838 800" />
        </svg>
      </div>

      <nav className="la-nav" aria-label="Main">
        <div className="la-nav-inner">
          <a className="la-nav-brand" href="#top" aria-label="La Cafe & Bistro home" data-testid="nav-brand-link">
            <img src={LOGO_SRC} alt="" />
            <span>La Cafe <em>&amp;</em> Bistro</span>
          </a>
          <ul className="la-nav-links">
            <li><a href="#" onClick={handleViewMenu} data-testid="nav-link-menu">Menu</a></li>
            <li><a href="#reviews" data-testid="nav-link-reviews">Reviews</a></li>
            <li><a href="#visit" data-testid="nav-link-visit">Visit us</a></li>
          </ul>
          <div className="la-nav-right">
            {orderingOpen && (
              <a className="la-nav-cta" href="#" onClick={handleOrderOnline} data-testid="nav-cta-order">Order</a>
            )}
            <button
              type="button"
              className={`la-hamburger ${menuOpen ? 'is-open' : ''}`}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="la-mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="nav-hamburger"
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </nav>

      <div
        id="la-mobile-menu"
        className={`la-mobile-menu ${menuOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
        onClick={() => setMenuOpen(false)}
      >
        <ul onClick={(e) => e.stopPropagation()}>
          <li><a href="#" onClick={(e) => { setMenuOpen(false); handleViewMenu(e); }} data-testid="mobile-nav-menu">Menu</a></li>
          <li><a href="#reviews" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-reviews">Reviews</a></li>
          <li><a href="#visit" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-visit">Visit us</a></li>
          {orderingOpen && (
            <li>
              <a
                href="#"
                className="la-mobile-menu-cta"
                onClick={(e) => { setMenuOpen(false); handleOrderOnline(e); }}
                data-testid="mobile-nav-order"
              >Order online</a>
            </li>
          )}
        </ul>
      </div>

      <header className="la-hero" id="top">
        <div className="la-logo-halo">
          <img className="la-hero-logo" src={LOGO_SRC} alt="La Cafe & Bistro logo" />
        </div>
        <p className="la-tagline">Warrington &middot; Est. 2015</p>
        <h1>Fresh food. Proper coffee.<br /><span className="la-grad">A warm welcome.</span></h1>
        <p className="la-sub">All-day breakfast, homemade lunches and barista-made coffee, cooked fresh every morning in the heart of Warrington.</p>
        <div className="la-hero-ctas">
          <a className="la-btn-pill" href="#" onClick={handleViewMenu} data-testid="hero-cta-menu">View the menu</a>
          <a className="la-btn-ghost" href="#visit" data-testid="hero-cta-visit">Find us</a>
        </div>
        <div className="la-hero-meta">
          <span>Open today <strong>8:00 – 14:00</strong></span>
        </div>
      </header>

      <main className="la-shell">
        <div className="la-tiles">
          <section className="la-tile full la-reveal" id="menu">
            <p className="la-eyebrow">The menu</p>
            <h2>Everything made to order.</h2>
            <p>From full English breakfasts to daily specials — every plate is prepared fresh in our kitchen, with locally sourced ingredients wherever we can.</p>
            <div className="la-links">
              <a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-browse-menu">Browse the full menu</a>
              <a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-specials">See today&apos;s specials</a>
            </div>
          </section>

          <section className="la-tile la-reveal" id="breakfast">
            <span className="la-ic" aria-hidden="true">
              <svg viewBox="0 0 24 24"><circle cx="10" cy="12" r="7.2" /><circle cx="10" cy="12" r="2.6" /><path d="M17.2 12h4.6" /></svg>
            </span>
            <p className="la-eyebrow">Breakfast</p>
            <h2>Served all day.</h2>
            <p>Full English, veggie stacks, smashed avocado and warm pastries — breakfast doesn&apos;t watch the clock here.</p>
            <div className="la-links"><a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-breakfast">Explore breakfast</a></div>
          </section>

          <section className="la-tile la-reveal" id="coffee">
            <span className="la-ic" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4 10h12v5a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5v-5z" /><path d="M16 11h2a2.5 2.5 0 0 1 0 5h-2" /><path d="M8.5 3.5c-.8 1 .8 1.6 0 2.6M12 3.5c-.8 1 .8 1.6 0 2.6" /></svg>
            </span>
            <p className="la-eyebrow">Coffee</p>
            <h2>Barista-made, every cup.</h2>
            <p>Locally roasted beans and silky flat whites brewed with care, every cup.</p>
            <div className="la-links"><a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-coffee">Coffee &amp; drinks</a></div>
          </section>

          <section className="la-tile la-reveal">
            <span className="la-ic" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4.5 15.5a7.5 7.5 0 0 1 15 0" /><path d="M2.8 15.5h18.4" /><path d="M12 8V6.8" /><circle cx="12" cy="5.6" r="1.1" /><path d="M5 18.5h14" /></svg>
            </span>
            <p className="la-eyebrow">Lunch</p>
            <h2>Big on flavour.</h2>
            <p>Toasted paninis, hearty jacket potatoes and fresh salads that change with the seasons.</p>
            <div className="la-links"><a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-lunch">See lunch options</a></div>
          </section>

          <section className="la-tile la-reveal">
            <span className="la-ic" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4 13h16v6H4z" /><path d="M4 16c1.3 0 1.3-1.2 2.7-1.2S8 16 9.3 16s1.4-1.2 2.7-1.2 1.4 1.2 2.7 1.2 1.3-1.2 2.6-1.2S18.7 16 20 16" /><path d="M12 13V9.5" /><circle cx="12" cy="7" r="1.6" /></svg>
            </span>
            <p className="la-eyebrow">Sweet treats</p>
            <h2>Baked in-house.</h2>
            <p>Cakes, traybakes and fresh scones every morning — the perfect excuse for one more coffee.</p>
            <div className="la-links"><a className="la-link-cta" href="#" onClick={handleViewMenu} data-testid="tile-cta-cakes">Cakes &amp; bakes</a></div>
          </section>

          <section className="la-tile full la-reveal" id="reviews">
            <p className="la-eyebrow">What people say</p>
            <h2>Loved by locals.</h2>
            <div className="la-reviews">
              <article className="la-review">
                <span className="la-stars" aria-label="5 out of 5 stars">★★★★★</span>
                <q>Best breakfast in Warrington, hands down. Everything cooked fresh and the staff are so friendly.</q>
                <cite>Sarah M.</cite>
              </article>
              <article className="la-review">
                <span className="la-stars" aria-label="5 out of 5 stars">★★★★★</span>
                <q>The flat white is genuinely the best I&apos;ve had outside Manchester. Lovely spot to work from too.</q>
                <cite>James T.</cite>
              </article>
              <article className="la-review">
                <span className="la-stars" aria-label="5 out of 5 stars">★★★★★</span>
                <q>Took the dog along and they brought him water before we&apos;d even ordered. Proper local gem.</q>
                <cite>Priya K.</cite>
              </article>
            </div>
          </section>

          <section className="la-tile full la-dark la-reveal" id="visit">
            <p className="la-eyebrow">Visit us</p>
            <h2>Right here in Warrington.</h2>
            <p>Pop in for a bite, a brew, or both. Dogs welcome in our outdoor seating area.</p>
            <div className="la-visit-grid">
              <div className="la-visit-info">
                <h3>Find us</h3>
                <p>
                  La Cafe &amp; Bistro<br />
                  55A Westy Ln, Latchford East<br />
                  Warrington WA4 1QJ<br />
                  United Kingdom<br /><br />
                  <a href="tel:+447598561008">07598 561008</a><br />
                  <a href="mailto:hello@lacafebistro.co.uk">hello@lacafebistro.co.uk</a>
                </p>
              </div>
              <div>
                <h3>Opening hours</h3>
                <dl className="la-hours">
                  <dt>Monday – Saturday</dt><dd>8:00 – 14:00</dd>
                  <dt>Sunday</dt><dd>9:00 – 14:00</dd>
                </dl>
              </div>
            </div>
            <div className="la-links">
              <a
                className="la-link-cta"
                href="https://www.google.com/maps/search/?api=1&query=55A+Westy+Ln%2C+Latchford+East%2C+Warrington+WA4+1QJ"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-cta-directions"
              >
                Get directions
              </a>
            </div>
          </section>
        </div>
      </main>

      <footer className="la-footer">
        <div className="la-foot-inner">
          <div className="la-foot-cols">
            <div className="la-foot-brand">
              <img src={LOGO_SRC} alt="La Cafe & Bistro" />
              <p>Fresh food, proper coffee and a warm welcome in the heart of Warrington.</p>
            </div>
            <div>
              <h4>Explore</h4>
              <ul>
                <li><a href="#menu">Menu</a></li>
                <li><a href="#breakfast">Breakfast</a></li>
                <li><a href="#coffee">Coffee &amp; drinks</a></li>
                <li><a href="#reviews">Reviews</a></li>
              </ul>
            </div>
            <div>
              <h4>Visit</h4>
              <ul>
                <li><a href="#visit">Find us</a></li>
                <li><a href="#visit">Opening hours</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate('/contact-us'); }}>Contact</a></li>
              </ul>
            </div>
            <div>
              <h4>Hours</h4>
              <ul>
                <li>Mon – Sat&nbsp; 8:00 – 14:00</li>
                <li>Sunday&nbsp; 9:00 – 14:00</li>
              </ul>
            </div>
          </div>
          <div className="la-fine">
            <p>Opening hours may vary on bank holidays. Please let our team know about any allergies or dietary requirements — full allergen information is available on request.</p>
            <p style={{ marginTop: 10 }}>© 2015 La Cafe &amp; Bistro, Warrington, United Kingdom. All rights reserved. Developed by JPTECK LTD.</p>
          </div>
        </div>
      </footer>

      <LocationPickerModal
        open={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        locations={locations}
        onSelect={handleLocationSelect}
      />

      <div className="la-mobile-bar" role="navigation" aria-label="Quick actions">
        <a
          className="la-mb-btn la-mb-call"
          href="tel:+447598561008"
          data-testid="mobile-bar-call"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4.5 5.2c.2-1 1.1-1.7 2.1-1.7h2c.9 0 1.7.6 1.9 1.5l.7 2.7c.2.8-.1 1.7-.8 2.2L9 11c1.2 2.6 3.3 4.7 5.9 5.9l1.2-1.4c.5-.6 1.4-.9 2.2-.7l2.7.7c.9.2 1.5 1 1.5 1.9v2c0 1.1-.8 2-1.9 2.1-9.5 1-17.6-7.1-16.6-16.6.1-.9.5-1.4 1.5-1.7z" />
          </svg>
          <span>Call us</span>
        </a>
        <a
          className="la-mb-btn la-mb-map"
          href="https://www.google.com/maps/search/?api=1&query=55A+Westy+Ln%2C+Latchford+East%2C+Warrington+WA4+1QJ"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-bar-directions"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.5c-3.9 0-7 3.1-7 7 0 5.2 7 12 7 12s7-6.8 7-12c0-3.9-3.1-7-7-7zm0 9.6a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2z" />
          </svg>
          <span>Get directions</span>
        </a>
      </div>
    </div>
  );
};

export default HomeLanding;
